/**
 * VDIV-Kontenrahmen — Verwaltung.
 * Route: /weg/konten
 *
 * Zeigt alle WEG-Konten des Mandanten (nach Kategorie gruppiert).
 * Beim ersten Öffnen wird der VDIV-Musterkontenrahmen automatisch geseedet.
 * Konten sind editierbar (Name, Standard-Schlüssel, aktiv/inaktiv).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Coins, PencilSimple, Plus, ArrowClockwise, ArrowLeft } from "@phosphor-icons/react";

const KEY_LABEL = {
  mea: "MEA", head: "Kopf", consumption: "Verbrauch", area: "Fläche", none: "Keine",
};
const KIND_LABEL = { income: "Einnahme", expense: "Ausgabe" };
const CATEGORY_ORDER = ["einnahmen", "betriebskosten", "versicherungen", "heizung", "verwaltung", "instandhaltung", "ruecklage"];
const CATEGORY_LABEL = {
  einnahmen: "Einnahmen", betriebskosten: "Bewirtschaftungskosten",
  versicherungen: "Versicherungen", heizung: "Heizung",
  verwaltung: "Verwaltung", instandhaltung: "Instandhaltung",
  ruecklage: "Instandhaltungsrücklage",
};

export default function WegKonten() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = () =>
    api.get(`/weg/accounts?include_inactive=${showInactive}`)
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  useEffect(() => { load(); }, [showInactive]); // eslint-disable-line react-hooks/exhaustive-deps

  const reseed = async () => {
    const r = await api.post("/weg/accounts/reseed");
    toast.success(`${r.data.inserted} VDIV-Konten hinzugefügt`);
    load();
  };

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (acc) => { setEditing(acc); setDialogOpen(true); };

  const grouped = rows.reduce((acc, r) => {
    const cat = r.category || "sonstige";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(r);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="weg-konten-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs">
            <Link to="/weg" className="text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"><ArrowLeft size={12} /> WEG-Verwaltung</Link>
          </div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2 mt-1">
            <Coins weight="duotone" className="text-emerald-700" /> Kontenrahmen (VDIV-Muster)
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Grundlage für Wirtschaftspläne und Jahresabrechnungen. Kann pro Mandant angepasst werden.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-600 flex items-center gap-2">
            <Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="show-inactive-toggle" />
            Inaktive anzeigen
          </label>
          <Button variant="outline" size="sm" onClick={reseed} data-testid="reseed-btn">
            <ArrowClockwise size={13} className="mr-1.5" /> VDIV nachladen
          </Button>
          <Button size="sm" onClick={openNew} data-testid="account-new-btn">
            <Plus size={13} className="mr-1.5" /> Neues Konto
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Lade …</div>
      ) : (
        CATEGORY_ORDER.filter((c) => grouped[c]?.length).map((cat) => (
          <section key={cat} className="space-y-2" data-testid={`weg-konten-section-${cat}`}>
            <h2 className="text-sm font-semibold text-slate-700">{CATEGORY_LABEL[cat] || cat}</h2>
            <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2 w-24">Konto-Nr.</th>
                    <th className="text-left px-3 py-2">Bezeichnung</th>
                    <th className="text-center px-3 py-2">Art</th>
                    <th className="text-center px-3 py-2">Std.-Schlüssel</th>
                    <th className="text-center px-3 py-2">Rücklage</th>
                    <th className="text-center px-3 py-2">Status</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {grouped[cat].map((a) => (
                    <tr key={a.id} data-testid={`account-row-${a.account_number}`} className={!a.is_active ? "opacity-50" : ""}>
                      <td className="px-3 py-2 font-mono text-xs font-semibold">{a.account_number}</td>
                      <td className="px-3 py-2">{a.name}</td>
                      <td className="px-3 py-2 text-center">
                        <Badge variant="outline" className={`text-[10px] ${a.kind === "income" ? "bg-emerald-50 text-emerald-700" : "bg-slate-50 text-slate-700"}`}>
                          {KIND_LABEL[a.kind]}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-center text-xs text-slate-600">{KEY_LABEL[a.default_distribution_key]}</td>
                      <td className="px-3 py-2 text-center">
                        {a.is_reserve && <Badge className="bg-amber-100 text-amber-800 text-[10px]">Rücklage</Badge>}
                      </td>
                      <td className="px-3 py-2 text-center text-xs">
                        {a.is_active ? <span className="text-emerald-600">aktiv</span> : <span className="text-slate-400">inaktiv</span>}
                      </td>
                      <td className="px-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(a)} data-testid={`account-edit-${a.account_number}`}>
                          <PencilSimple size={13} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))
      )}

      <AccountDialog
        open={dialogOpen}
        editing={editing}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        onSaved={() => { setDialogOpen(false); setEditing(null); load(); }}
      />
    </div>
  );
}

function AccountDialog({ open, editing, onClose, onSaved }) {
  const [form, setForm] = useState({
    account_number: "", name: "", kind: "expense",
    default_distribution_key: "mea", category: "betriebskosten",
    is_reserve: false, is_active: true, notes: "",
  });
  useEffect(() => {
    if (editing?.id) {
      setForm({
        account_number: editing.account_number || "",
        name: editing.name || "",
        kind: editing.kind || "expense",
        default_distribution_key: editing.default_distribution_key || "mea",
        category: editing.category || "betriebskosten",
        is_reserve: !!editing.is_reserve,
        is_active: editing.is_active !== false,
        notes: editing.notes || "",
      });
    } else if (open) {
      setForm({
        account_number: "", name: "", kind: "expense",
        default_distribution_key: "mea", category: "betriebskosten",
        is_reserve: false, is_active: true, notes: "",
      });
    }
  }, [editing, open]);

  const save = async () => {
    try {
      if (editing?.id) {
        await api.patch(`/weg/accounts/${editing.id}`, form);
        toast.success("Konto aktualisiert");
      } else {
        await api.post("/weg/accounts", form);
        toast.success("Konto angelegt");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="account-dialog">
        <DialogHeader>
          <DialogTitle>{editing?.id ? `Konto ${editing.account_number} bearbeiten` : "Neues Konto"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-slate-600 block">Konto-Nr. *
              <Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} disabled={!!editing?.id} data-testid="account-number-input" />
            </label>
            <label className="text-xs text-slate-600 block">Art
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="account-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="expense">Ausgabe</SelectItem>
                  <SelectItem value="income">Einnahme</SelectItem>
                </SelectContent>
              </Select>
            </label>
          </div>
          <label className="text-xs text-slate-600 block">Bezeichnung *
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="account-name-input" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-slate-600 block">Standard-Verteilungsschlüssel
              <Select value={form.default_distribution_key} onValueChange={(v) => setForm({ ...form, default_distribution_key: v })}>
                <SelectTrigger data-testid="account-key-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(KEY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
            <label className="text-xs text-slate-600 block">Kategorie
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
          </div>
          <div className="flex items-center gap-6 pt-1">
            <label className="text-xs text-slate-700 flex items-center gap-2">
              <Switch checked={form.is_reserve} onCheckedChange={(v) => setForm({ ...form, is_reserve: v })} />
              Rücklage
            </label>
            <label className="text-xs text-slate-700 flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              Aktiv
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button
            onClick={save}
            disabled={!form.account_number.trim() || !form.name.trim()}
            data-testid="account-save-btn"
          >
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
